import { motion } from 'framer-motion';

import { colorPalette } from '@lib/constants';
import { defaultColor } from '@components/Presentation/constants';
import { parseColorToRgba } from '@services/interactions-service';
import { type InteractionColors } from '@components/Presentation/types';

import type { LeaderboardRowSize, LeaderboardRowData } from './types';

export type LeaderboardRowColors = Partial<InteractionColors>;

/**
 * Geometry per size variant. `full` is the live stream view, `compact` the analytics
 * result view, `preview` the fixed-height theme previewer box.
 */
const SIZES: Record<LeaderboardRowSize, { row: string; avatar: string; initial: string | null }> = {
  full: { row: 'h-20', avatar: 'h-20 w-20', initial: 'text-3xl' },
  compact: { row: 'h-14', avatar: 'h-14 w-14', initial: 'text-2xl' },
  // `initial: null` scales the avatar letter with `fontSize` instead of pinning
  // it to a step, since the preview box has no fixed pixel height.
  preview: { row: 'h-[17%] min-h-10', avatar: 'h-full aspect-square', initial: null },
};

/** The leaderboard falls back to the poll palette, which is what it shipped with. */
const chartColorOf = (colors?: LeaderboardRowColors) => colors?.leaderboardChartColor
  || colors?.pollChartColor
  || defaultColor.leaderboardChartColor;

export const resolveBarColor = (colors?: LeaderboardRowColors) => parseColorToRgba(chartColorOf(colors), 1);

/**
 * Falls back to black when the configured label colour is indistinguishable from
 * the bar it sits on.
 */
export const resolveTextColor = (colors?: LeaderboardRowColors) => {
  const textColor = colors?.leaderboardLabelColor
    || colors?.pollLabelColor
    || defaultColor.leaderboardLabelColor;
  return parseColorToRgba(textColor, 1) === resolveBarColor(colors) ? '#000000' : textColor;
};

interface LeaderboardRowProps {
  leader: LeaderboardRowData;
  index: number;
  colors?: LeaderboardRowColors;
  fontFamily?: string;
  fontSize?: string;
  size?: LeaderboardRowSize;
  /** Animate the bar filling in. Off for static previews. */
  animate?: boolean;
}

/**
 * A single score bar. Purely presentational and free of framer-motion layout
 * concerns, so both the live board and the theme previewer render the exact same
 * markup — which is what keeps previews honest.
 */
const LeaderboardRow = ({
  leader,
  index,
  colors,
  fontFamily = 'Inter',
  fontSize = '32px',
  size = 'full',
  animate = true,
}: LeaderboardRowProps) => {
  const { commenterName, totalScore, relativePercentage } = leader;
  const geometry = SIZES[size];
  const barColor = resolveBarColor(colors);
  const textColor = resolveTextColor(colors);
  const width = `${relativePercentage}%`;

  return (
    <div
      style={{ background: parseColorToRgba(chartColorOf(colors), 0.4) }}
      className={`relative w-full ${geometry.row} rounded-[50px] transition-all overflow-hidden`}
    >
      {animate ? (
        <motion.div
          style={{ background: barColor }}
          className="absolute inset-y-0 left-0"
          initial={{ width: 0 }}
          animate={{ width }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
      ) : (
        <div style={{ background: barColor, width }} className="absolute inset-y-0 left-0" />
      )}

      <div className="relative z-10 w-full h-full flex items-center justify-between gap-4 pr-6">
        <div className="flex items-center gap-4 min-w-0 h-full">
          <div
            className={`${geometry.avatar} rounded-full flex justify-center items-center shrink-0`}
            style={{ fontFamily, backgroundColor: colorPalette[index % colorPalette.length] }}
          >
            <div
              className={`text-[#212121] ${geometry.initial ?? ''} font-semibold leading-none`}
              style={geometry.initial ? undefined : { fontSize }}
            >
              {commenterName?.charAt(0)}
            </div>
          </div>
          <div
            style={{ fontFamily, fontSize, color: textColor }}
            className="font-normal leading-none line-clamp-1"
          >
            {commenterName}
          </div>
        </div>

        <div
          style={{ fontFamily, fontSize, color: textColor }}
          className="font-normal leading-none shrink-0"
        >
          {totalScore || 0}
          p
        </div>
      </div>
    </div>
  );
};

export default LeaderboardRow;
